import React from 'react';
import { Linking, Platform, Pressable, ScrollView, Text, View } from 'react-native';

function P({ children }: { children: React.ReactNode }) {
  return <Text className="mb-3 text-base leading-6 text-zinc-800 dark:text-zinc-200">{children}</Text>;
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <Text className="mb-3 mt-6 text-lg font-bold text-zinc-900 dark:text-zinc-100 first:mt-0">{children}</Text>
  );
}

/** Kelimesi kelimesine yasal metin — değiştirilmemelidir. */
const ZERO_TOLERANCE_OFFICIAL_TR =
  'Platformumuzda sakıncalı içeriklere ve küfürlü/tacizkar kullanıcılara karşı sıfır tolerans (zero tolerance) politikası uygulanmaktadır. Kural ihlali yapan kullanıcılar 24 saat içinde sistemden uzaklaştırılır.';

type TermsOfServiceWebPageProps = {
  /** Web’de /terms-of-service için ana sayfa linki; uygulama içi ekranda false. */
  showHomeLink?: boolean;
};

export function TermsOfServiceWebPage({ showHomeLink = true }: TermsOfServiceWebPageProps) {
  const isWeb = Platform.OS === 'web';

  return (
    <ScrollView className="flex-1 bg-white dark:bg-black">
      <View className="mx-auto w-full max-w-3xl px-5 py-10">
        {showHomeLink && isWeb ? (
          <Pressable
            accessibilityRole="link"
            onPress={() => {
              if (typeof window !== 'undefined') {
                window.location.href = '/';
              }
            }}
            className="mb-6 self-start active:opacity-70">
            <Text className="text-base font-semibold text-rose-700">← Ana sayfa</Text>
          </Pressable>
        ) : null}

        <Text className="mb-2 text-3xl font-bold text-zinc-950 dark:text-zinc-50">
          Kullanım Koşulları (EULA)
        </Text>
        <Text className="mb-2 text-sm text-zinc-500 dark:text-zinc-400">
          Son güncelleme: 29 Nisan 2026
        </Text>
        <Text className="mb-8 text-sm italic text-zinc-600 dark:text-zinc-400">
          (English note for reviewers: This document is the End User License Agreement and Terms of
          Service for the Annedaş mobile and web applications.)
        </Text>

        <P>
          Bu Kullanım Koşulları (“Sözleşme”), Annedaş mobil ve web uygulamaları ile sunulan hizmetlere
          (“Hizmet”) erişiminizi ve kullanımınızı düzenler. Hizmeti kullanarak veya kayıt olarak bu
          Sözleşme ile{' '}
          <Text className="font-semibold text-zinc-900 dark:text-zinc-100">
            Gizlilik Politikası
          </Text>
          &apos;nı okuduğunuzu, anladığınızı ve bağlı kalmayı kabul ettiğinizi beyan edersiniz. Kabul
          etmiyorsanız lütfen Hizmeti kullanmayın.
        </P>

        <H2>1. Tanımlar</H2>
        <P>
          <Text className="font-semibold text-zinc-900 dark:text-zinc-100">“Şirket” / “Biz”:</Text>{' '}
          Annedaş uygulamasını işleten tarafları ifade eder.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900 dark:text-zinc-100">“Kullanıcı” / “Siz”:</Text>{' '}
          Hizmete erişen veya hesap oluşturan gerçek kişiyi ifade eder.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900 dark:text-zinc-100">“İçerik”:</Text> Forum
          gönderileri, yorumlar, mesajlar, profil bilgileri, pazar yeri ilanları, fotoğraflar ve Hizmet
          üzerinden yüklediğiniz veya paylaştığınız tüm materyalleri kapsar.
        </P>

        <H2>2. Hizmetin niteliği ve yaş sınırı</H2>
        <P>
          Annedaş; ebeveynler için tasarlanmış bir topluluk ve pazar yeri platformudur. Hizmet, reşit
          kullanıcılar (18 yaş ve üzeri) içindir. 18 yaşından küçükseniz veya yasal olarak bağlayıcı
          sözleşme kurma ehliyetiniz yoksa Hizmeti kullanamazsınız.
        </P>

        <H2>3. Hesap, güvenlik ve doğruluk</H2>
        <P>
          Kayıt sırasında doğru ve güncel bilgi vermekle yükümlüsünüz. Hesap kimlik bilgilerinizin
          gizliliğinden ve hesabınız altında yapılan tüm işlemlerden siz sorumlusunuz. Yetkisiz
          kullanımı derhal bildirmeniz beklenir.
        </P>

        <H2>4. Kabul edilen davranış ve yasaklar</H2>
        <P>Hizmeti yalnızca yürürlükteki hukuka ve bu Sözleşmeye uygun şekilde kullanmayı kabul edersiniz. Özellikle yasaktır:</P>
        <P>
          • Başkalarına yönelik taciz, tehdit, aşağılama, nefret söylemi veya stalking davranışları.
        </P>
        <P>• Müstehcen, şiddet içeren, yanıltıcı veya yasa dışı ürün/hizmet tanıtımı.</P>
        <P>• Başkasının kişisel verisini izinsiz paylaşmak (doxing).</P>
        <P>• Spam, otomatik botlar veya Hizmeti kötüye kullanan teknik müdahaleler.</P>
        <P>• Güvenlik önlemlerini aşmaya veya sistemlere zarar vermeye yönelik girişimler.</P>
        <P>• Çocukların cinsel istismarı veya sömürüsüne yönelik her türlü içerik veya davranış.</P>

        <H2>5. Sıfır tolerans ve yaptırım (resmi hüküm)</H2>
        <View className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 dark:border-rose-900 dark:bg-rose-950/40">
          <Text className="text-base font-semibold leading-6 text-zinc-900 dark:text-zinc-100">
            {ZERO_TOLERANCE_OFFICIAL_TR}
          </Text>
        </View>
        <Text className="mb-3 text-base italic leading-6 text-zinc-600 dark:text-zinc-400">
          (English / referee summary — same policy: The platform applies a zero-tolerance policy
          toward harmful content and users who engage in profanity or harassment. Users who violate
          the rules may be removed from the system within 24 hours.)
        </Text>
        <P>
          Şikayet incelemeleri, ihlalin ağırlığına ve tekrarına göre yapılır. Ağır ihlallerde süre
          beklemeden kalıcı hesap kapatma uygulanabilir. Hafif veya tek seferlik ihlallerde uyarı veya
          geçici kısıtlama da kullanılabilir; ancak yukarıdaki resmi cümle, politikanın özünü ifade
          eder ve moderasyon kararları bu çerçevede yürütülür.
        </P>

        <H2>6. Moderasyon ve şikayetler</H2>
        <P>
          Uygulama içi “Şikayet Et” ve benzeri araçlarla ihlal bildirimi yapılabilir. Şirket, güvenli
          ve saygılı bir ortam sağlamak için içeriği ön inceleme, kaldırma, gizleme veya hesap
          aksiyonları alma hakkını saklı tutar. Moderasyon kararları nihai değildir; itiraz için
          iletişim kanalları kullanılabilir ancak Şirketin makul ticari takdir yetkisi vardır.
        </P>

        <H2>7. Kullanıcı içeriği ve lisans</H2>
        <P>
          Yüklediğiniz İçerik üzerinde fikri mülkiyet haklarınız size aittir; ancak Hizmeti
          kullanarak, İçeriğinizi Hizmeti işletmek, göstermek, çoğaltmak (teknik olarak gerekli
          ölçüde), dağıtmak ve iyileştirmek için Şirkete dünya çapında, telifsiz, devredilebilir ve
          alt lisans verilebilir bir lisans vermiş olursunuz. İçeriğin yasal olduğunu ve üçüncü
          kişilerin haklarını ihlal etmediğini beyan edersiniz.
        </P>

        <H2>8. Fikri mülkiyet (Annedaş)</H2>
        <P>
          Uygulama adı, tasarım öğeleri, yazılım ve markalar Şirketin veya lisans verenlerinin
          mülkiyetindedir. İzinsiz kopyalama, tersine mühendislik veya ticari kullanım yasaktır.
        </P>

        <H2>9. Üçüncü taraf hizmetleri</H2>
        <P>
          Kimlik doğrulama, barındırma ve analitik gibi işlevler için (ör. Firebase) üçüncü taraf
          sağlayıcılar kullanılabilir. Bu sağlayıcıların kullanımı Gizlilik Politikası ve ilgili
          sağlayıcı şartlarına tabidir.
        </P>

        <H2>10. Feragatname ve sorumluluk sınırı</H2>
        <P>
          Hizmet “olduğu gibi” sunulur. Yasal olarak izin verilen azami ölçüde, Şirket; Hizmetin
          kesintisiz veya hatasız olacağına dair garanti vermez. Dolaylı, arızi veya sonuç olarak
          doğan zararlardan sorumluluk, yürürlükteki hukukun izin verdiği ölçüde sınırlanır.
        </P>

        <H2>11. Tazminat</H2>
        <P>
          İçeriğiniz veya Hizmeti kullanımınızın bu Sözleşmeyi veya üçüncü kişi haklarını ihlal
          etmesinden doğan iddia ve zararlara karşı Şirketi, yöneticilerini ve çalışanlarını savunmayı
          ve zararsız tutmayı kabul edersiniz.
        </P>

        <H2>12. Sözleşmenin feshi</H2>
        <P>
          Hesabınızı uygulama içi silme akışlarıyla kapatabilirsiniz. Şirket, ihlal veya risk
          durumunda hesabınızı askıya alabilir veya sonlandırabilir. Fesih sonrası yürürlükte kalması
          gereken hükümler (ör. sorumluluk sınırı, uyuşmazlık çözümü) geçerliliğini korur.
        </P>

        <H2>13. Değişiklikler</H2>
        <P>
          Bu Sözleşmeyi zaman zaman güncelleyebiliriz. Önemli değişikliklerde uygulama içi bildirim
          veya e-posta ile bilgilendirme yapılabilir. Güncelleme sonrası Hizmeti kullanmaya devam
          etmeniz, yeni koşulları kabul ettiğiniz anlamına gelebilir.
        </P>

        <H2>14. Uygulanacak hukuk ve yetkili merciler</H2>
        <P>
          Uyuşmazlıklarda tarafların hak ve yükümlülükleri, kullanıcının ikamet ettiği ülkenin
          zorunlu tüketici koruma kuralları saklı kalmak kaydıyla, Şirketin faaliyet gösterdiği
          yargı bölgesinin hukukuna tabi olabilir. Zorunlu hukuk aksini öngörüyorsa, tüketici
          lehine olan düzenlemeler uygulanır.
        </P>

        <H2>15. İletişim</H2>
        <P>
          Kullanım Koşulları ile ilgili sorularınız için:{' '}
          <Text
            accessibilityRole="link"
            className="text-rose-700 underline dark:text-rose-400"
            onPress={() => {
              Linking.openURL('mailto:chnakbs@gmail.com').catch(() => {});
            }}>
            chnakbs@gmail.com
          </Text>
        </P>

        <View className="h-16" />
      </View>
    </ScrollView>
  );
}
